/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { LoginDetails, UtenteLoginHistory } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene il profilo dell'utente attualmente loggato, comprensivo della login history e di eventuali ulteriori informazioni annesse.
 * @summary login history dell'utente
 */
export const getLoginHistory = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<UtenteLoginHistory>(
    { url: `/framework-cfg-utenti/login-history`, method: 'get', signal },
    options,
  );
};

export const getGetLoginHistoryQueryKey = () =>
  [`/framework-cfg-utenti/login-history`] as const;

export const getGetLoginHistoryQueryOptions = <
  TData = Awaited<ReturnType<typeof getLoginHistory>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getLoginHistory>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getLoginHistory>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLoginHistoryQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLoginHistory>>> = ({
    signal,
  }) => getLoginHistory(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetLoginHistoryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLoginHistory>>
>;
export type GetLoginHistoryQueryError = ErrorType<unknown>;

/**
 * @summary login history dell'utente
 */
export const useGetLoginHistory = <
  TData = Awaited<ReturnType<typeof getLoginHistory>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getLoginHistory>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetLoginHistoryQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Salvataggio di un record di login info per la login history.
 * @summary Salva login info
 */
export const postLoginHistory = (
  loginDetails: LoginDetails,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/login-history`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: loginDetails,
    },
    options,
  );
};

export const getPostLoginHistoryMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLoginHistory>>,
    TError,
    { data: LoginDetails },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postLoginHistory>>,
  TError,
  { data: LoginDetails },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postLoginHistory>>,
    { data: LoginDetails }
  > = props => {
    const { data } = props ?? {};

    return postLoginHistory(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostLoginHistoryMutationResult = NonNullable<
  Awaited<ReturnType<typeof postLoginHistory>>
>;
export type PostLoginHistoryMutationBody = LoginDetails;
export type PostLoginHistoryMutationError = ErrorType<unknown>;

/**
 * @summary Salva login info
 */
export const usePostLoginHistory = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLoginHistory>>,
    TError,
    { data: LoginDetails },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostLoginHistoryMutationOptions(options);

  return useMutation(mutationOptions);
};
